import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  AccountUserDetailDTO,
  ChangeOwnPasswordSchema,
  ChangePasswordDTO,
  ChangePasswordSchema,
  FieldIssue,
  Paths,
  UserDetailDTO
} from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { AuthContext } from '_lib/featureComponents/auth/authContext';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';

import { AsyncDialogProps, useAsyncDialog } from './useAsyncDialog';

export type UpdatePasswordDialogProps = AsyncDialogProps & {
  user: AccountUserDetailDTO | UserDetailDTO;
};

// TODO: rethink this!
const handledIssues: { [k in Paths<ChangePasswordDTO>]: k } = {
  currentPassword: 'currentPassword',
  password: 'password',
  passwordConfirm: 'passwordConfirm'
};

const UpdatePasswordDialog = (props: UpdatePasswordDialogProps) => {
  const { user, onClose, ...dialogProps } = props;

  const [updatePasswordState, setUpdatePasswordState] = useState({
    showCurrentPassword: false,
    showPassword: false,
    showPasswordConfirm: false,
    currentPassword: '',
    password: '',
    passwordConfirm: ''
  });

  const [fieldIssues, setFieldIssues] = useState<FieldIssue[]>([]);
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>([]);

  const api = useContext(ApiContext);
  const { authUserCacheResource, setAuthToken } = useContext(AuthContext);

  const accountUserChangePasswordMutation = api.accountUsers.useChangePassword();
  const ccUserChangePasswordMutation = api.users.useChangePassword();

  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();
  const isChangingOwnPassword = cachedAuthUserQuery?.data?.id === user.id;

  const handleCancel = () => {
    onClose({ action: 'CANCEL' });
  };

  const handleConfirm = async () => {
    const schema = isChangingOwnPassword ? ChangeOwnPasswordSchema : ChangePasswordSchema;

    const result = schema.safeParse({
      password: updatePasswordState.password,
      passwordConfirm: updatePasswordState.passwordConfirm,
      currentPassword: updatePasswordState.currentPassword
    });
    if (result.success) {
      try {
        const args = { id: user.id, data: result.data };

        const authTokenIfSelf = user.accountId
          ? await accountUserChangePasswordMutation.put(args)
          : await ccUserChangePasswordMutation.put(args);

        if (user.id === cachedAuthUserQuery.data?.id && authTokenIfSelf) {
          setAuthToken(authTokenIfSelf);
        }

        onClose({ action: 'CONFIRM' });
      } catch (err) {
        if (err instanceof BadRequestError) {
          setFieldIssues(err.responseData.fieldIssues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors') || []);
          setNonFieldErrors(err.responseData.nonFieldErrors);
        }
      }
    } else {
      // TODO: do this somewhere else as part of an error handling refactor
      setFieldIssues(result.error.issues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors'));
      setNonFieldErrors(
        result.error.issues
          .filter(({ path }) => !Object.values(handledIssues).find((issue) => issue === path.join('.')))
          .map(({ message }) => message)
      );
    }
  };

  const isFetchingOrPending =
    cachedAuthUserQuery.isFetching ||
    accountUserChangePasswordMutation.isPending ||
    ccUserChangePasswordMutation.isPending;

  return (
    <DialogWrapper onClose={onClose} titleText='Change Password' {...dialogProps}>
      <DialogContent sx={{ minWidth: '450px' }}>
        <BackdropSpinner open={isFetchingOrPending} />
        <Stack spacing={2.5} padding={2}>
          {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
          {isChangingOwnPassword && (
            <TextField
              type={updatePasswordState.showCurrentPassword ? 'text' : 'password'}
              label='Current Password'
              value={updatePasswordState.currentPassword}
              required
              onChange={(e) => setUpdatePasswordState((prev) => ({ ...prev, currentPassword: e.target.value }))}
              error={{
                issuePath: handledIssues.currentPassword,
                issues: fieldIssues
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton
                      onClick={() =>
                        setUpdatePasswordState((prev) => ({ ...prev, showCurrentPassword: !prev.showCurrentPassword }))
                      }
                      edge='end'>
                      {updatePasswordState.showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }
              }}
            />
          )}
          <TextField
            type={updatePasswordState.showPassword ? 'text' : 'password'}
            label='New Password'
            value={updatePasswordState.password}
            required
            onChange={(e) => setUpdatePasswordState((prev) => ({ ...prev, password: e.target.value }))}
            error={{
              issuePath: handledIssues.password,
              issues: fieldIssues
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    onClick={() => setUpdatePasswordState((prev) => ({ ...prev, showPassword: !prev.showPassword }))}
                    edge='end'>
                    {updatePasswordState.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }
            }}
          />
          <TextField
            type={updatePasswordState.showPasswordConfirm ? 'text' : 'password'}
            label='Confirm Password'
            value={updatePasswordState.passwordConfirm}
            required
            onChange={(e) => setUpdatePasswordState((prev) => ({ ...prev, passwordConfirm: e.target.value }))}
            error={{
              issuePath: handledIssues.passwordConfirm,
              issues: fieldIssues
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    onClick={() =>
                      setUpdatePasswordState((prev) => ({ ...prev, showPasswordConfirm: !prev.showPasswordConfirm }))
                    }
                    edge='end'>
                    {updatePasswordState.showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogButton color='cancel' onClick={handleCancel} text='Cancel' />
        <DialogButton onClick={handleConfirm} text='Confirm' disabled={isFetchingOrPending} />
      </DialogActions>
    </DialogWrapper>
  );
};

export const useUpdatePasswordDialog = () => useAsyncDialog(UpdatePasswordDialog);
